"use client";

import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Logo } from "@/components/Logo";

interface IntroLoaderProps {
  locale: string;
  phone: string;
  logoProps?: {
    ccn?: string;
    line1?: string;
    line2?: string;
    subtext?: string;
  };
}

export default function IntroLoader({
  locale,
  logoProps,
  phone,
}: IntroLoaderProps) {
  const [show, setShow] = useState(true);

  useEffect(() => {
    // Lock scrolling on mount
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    // Keep it visible for 2 seconds before initiating fade out
    const timer = setTimeout(() => {
      setShow(false);
    }, 2200);

    return () => {
      // Re-enable scroll on unmount
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, []);

  return (
    <AnimatePresence
      onExitComplete={() => {
        document.body.style.overflow = "";
        document.documentElement.style.overflow = "";
      }}
    >
      {show && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{
            opacity: 0,
            transition: { duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] },
          }}
          className="fixed inset-0 z-[99999] flex items-center justify-center bg-[#06191e]"
          style={{
            background:
              "radial-gradient(circle at center, #0e3d48 0%, #06191e 100%)",
          }}
        >
          {/* Subtle slow glowing background effect */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(219,182,83,0.08)_0%,transparent_60%)] animate-pulse-slow pointer-events-none" />

          {/* Logo container with scale/fade entry and zoom-out/fade exit */}
          <motion.div
            initial={{ opacity: 0, scale: 0.82 }}
            animate={{
              opacity: 1,
              scale: 1,
              transition: { duration: 0.9, ease: "easeOut" },
            }}
            exit={{
              opacity: 0,
              scale: 0.92,
              transition: { duration: 0.5, ease: "easeIn" },
            }}
            className="relative scale-[0.7] sm:scale-[0.9] md:scale-[1.1] lg:scale-[1.25] xl:scale-[1.35] px-6"
          >
            <Logo locale={locale} {...logoProps} phone={phone} />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
